import { useState, useCallback } from 'react'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import { useDocuments } from '@/features/bookkeeping/hooks/use-documents'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { UploadDocumentType } from '@/constants/businessConstants'
import type { IDocumentsStepContext } from '@/features/bookkeeping/end-of-year-review/types'
import type { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { handleDocumentDeletion } from '../document-deletion.helpers'
import './styles.scss'

export interface TaxReturnDocumentProps {
  stepContext: IDocumentsStepContext | undefined
  setStepContext: (context: IDocumentsStepContext) => void
  userDocuments: UserDocument[]
  isFollowUp: boolean
  taxYear: number
  stepStatus?: string
}

/**
 * TaxReturnDocument
 *
 * Reusable component for managing previous year tax return document uploads.
 * This is a provider-less document type (no payroll provider association).
 *
 * Features:
 * - Single document upload (Form 1120-S from previous tax year)
 * - "Not relevant" workflow with confirmation modal
 * - Embedded FileUploadModal for uploads
 * - Follow-up mode support (shows red background if reupload requested)
 * - Uses existing V1 UI components (DocumentUploadCard, FileUploadModal)
 *
 * Provider-less Pattern:
 * - Omits provider parameter when calling useDocuments hook functions
 * - Does NOT include provider field in document objects
 * - Avoids legacy empty string pattern
 *
 * @example
 * ```tsx
 * <TaxReturnDocument
 *   stepContext={taxStepContext}
 *   setStepContext={setTaxStepContext}
 *   userDocuments={allUserDocuments}
 *   isFollowUp={false}
 *   taxYear={2024}
 * />
 * ```
 */
export function TaxReturnDocument({
  stepContext,
  setStepContext,
  userDocuments,
  isFollowUp,
  taxYear,
  stepStatus,
}: TaxReturnDocumentProps) {
  const [uploadDialog, setUploadDialog] = useState(false)

  const { filterDocuments, getStatus } = useDocuments({
    stepContextDocuments: stepContext?.documents,
    userDocuments,
  })

  const previousTaxYear = (taxYear - 1).toString()
  const categoryCode = UserDocumentCategoryIdentifier.finalReturnForm1120s

  const handleUploadClick = useCallback(() => {
    setUploadDialog(true)
  }, [])

  const handleFileUploaded = useCallback(
    (uploadedFile: UploadedFile) => {
      const documentId = uploadedFile.id

      // Find the original document that needs re-upload (if it exists)
      const originalDocument = stepContext?.documents?.find(
        (doc) => doc.categoryCode === categoryCode
      )

      // Replace or add the document to the list
      // Preserve requestReUpload and adminNote from original, set updatedByUser: true
      // Only include fields that have values (omit undefined)
      const newDoc = {
        categoryCode,
        documentId,
        updatedByUser: true,
        ...(originalDocument?.adminNote && {
          adminNote: originalDocument.adminNote,
        }),
        ...(originalDocument?.requestReUpload !== undefined
          ? { requestReUpload: originalDocument.requestReUpload }
          : isFollowUp
            ? { requestReUpload: true }
            : {}),
      }

      // Update step context - replace existing tax return document
      setStepContext({
        ...stepContext,
        documents: [
          // Filter out existing tax return documents
          ...(stepContext?.documents?.filter(
            (doc) => doc.categoryCode !== categoryCode
          ) ?? []),
          newDoc,
        ],
      })

      setUploadDialog(false)
    },
    [categoryCode, isFollowUp, stepContext, setStepContext]
  )

  // Get document from step context for follow-up mode
  const taxDocument = stepContext?.documents?.find(
    (doc) => doc.categoryCode === categoryCode
  )

  // Handle document deletion - preserve requestReUpload state in follow-up mode
  const handleDocumentDeleted = useCallback(() => {
    const documents = handleDocumentDeletion(
      stepContext?.documents,
      (doc) => doc.categoryCode === categoryCode,
      isFollowUp
    )
    setStepContext({
      ...stepContext,
      documents,
    })
  }, [stepContext, setStepContext, categoryCode, isFollowUp])

  // Check if step requires user action - only render section if status is user_action_required
  if (isFollowUp && stepStatus !== 'user_action_required') {
    return null
  }

  return (
    <>
      <div className="tax-return-document">
        <h2>{`${previousTaxYear} Business Tax Return`}</h2>

        <DocumentUploadCard
          name={`${previousTaxYear} Business Tax Return (Form 1120-S)`}
          description="Your Form 1120-S from last year. Make sure you include all attachments (e.g. Schedule L and Schedule M-2)."
          status={getStatus(categoryCode)}
          documents={filterDocuments(categoryCode)}
          handleUploadBtn={handleUploadClick}
          isFollowUp={isFollowUp}
          isRequiredReviewed={isFollowUp ? taxDocument : false}
          onDocumentDeleted={handleDocumentDeleted}
        />
      </div>

      <FileUploadModal
        open={uploadDialog}
        close={() => setUploadDialog(false)}
        documentType={UploadDocumentType.TAX}
        categoryInternalName={categoryCode}
        userFacing
        year={previousTaxYear}
        setUploadedFile={handleFileUploaded}
      />
    </>
  )
}
